import React, { useEffect, useRef, useState } from "react";
import { App, Button, Input, Modal, Space, Table, Tag } from "antd";
import { useQuery } from "react-query";
import { useIntl } from "react-intl";
import jwtAxios from "../../../../auth/jwt-api";
import { useAuthDatas } from "../../../../auth/JWTAuthProvider";
import useDebounce from "../../../../utils/useDebounce";

const TYPE_COLORS = {
  sold: "blue",
  not_full_paid: "orange",
  reserved_paid: "gold",
  mortgage: "purple",
  reserved: "cyan",
};

export default function Orders() {
  const { formatMessage } = useIntl();
  const { message } = App.useApp();
  const { user } = useAuthDatas();
  const t = (id) => formatMessage({ id });
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 50,
  });

  const [searchClient, setSearchClient] = useState("");
  const [searchContract, setSearchContract] = useState("");

  const debouncedSetClient = useDebounce((value) => {
    setSearchClient(value);
    setPagination((prev) => ({ ...prev, current: 1 }));
  }, 500);

  const debouncedSetContract = useDebounce((value) => {
    setSearchContract(value);
    setPagination((prev) => ({ ...prev, current: 1 }));
  }, 500);
  const [iframeOpen, setIframeOpen] = useState(false);
  const [myIdLoading, setMyIdLoading] = useState(false);
  const selectedOrderRef = useRef(null);

  const { data, isLoading, refetch } = useQuery(
    ["/order", pagination.current, pagination.pageSize, searchClient, searchContract],
    () =>
      jwtAxios.get("/order", {
        params: {
          page: pagination.current,
          limit: pagination.pageSize,
          order: "createdAt",
          sortBy: "DESC",
          ...(searchClient && { clientName: searchClient }),
          ...(searchContract && { contractNumber: searchContract }),
        },
      }),
    { keepPreviousData: true }
  );

  const rows = data?.data?.data?.results?.rows ?? [];
  const total = data?.data?.data?.results?.count ?? 0;



  const runIdentify = async (photo) => {
    const order = selectedOrderRef.current;
    if (!order) return;
    const contractId = order.contracts?.[0]?.id;
    if (!contractId) {
      message.error("Shartnoma topilmadi");
      return;
    }
    setMyIdLoading(true);
    try {
      const response = await jwtAxios.post("/myId/identify", {
        front: photo,
        contractId,
        checkUser: false,
        director: true,
      });
      if (response?.data?.data?.status_code === 1) {
        message.success("Договор подписан через MyID");
        refetch();
      } else {
        message.warning(response?.data?.data?.status_text);
      }
    } catch (error) {
      const details = error?.response?.data?.error?.detail;
      if (Array.isArray(details)) {
        details.forEach((err) => {
          const msg = err?.msg || "";
          if (msg.toLowerCase().includes("pinfl")) {
            message.error("PINFL — неверно.");
          } else if (msg.toLowerCase().includes("birth_date")) {
            message.error("Неверная дата рождения");
          } else if (msg.toLowerCase().includes("minimum required age is 14")) {
            message.error("Минимальный возраст — 14 лет.");
          } else {
            message.error(msg || "Произошла ошибка при проверке MyID.");
          }
        });
      } else {
        message.error(
          error?.response?.data?.error || "Произошла ошибка при проверке MyID."
        );
      }
    } finally {
      setMyIdLoading(false);
    }
  };

  useEffect(() => {
    const handler = (e) => {
      if (e.data?.cmd === 2 && e?.data?.photo_from_camera) {
        const iframe = document.getElementById("orders_myid_iframe");
        if (iframe?.contentWindow) {
          iframe.contentWindow.postMessage({ cmd: "close" }, "*");
        }
        setIframeOpen(false);
        runIdentify(e.data.photo_from_camera);
      }
    };
    window.addEventListener("message", handler);
    return () => window.removeEventListener("message", handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openIdentify = (order) => {
    selectedOrderRef.current = order;
    setIframeOpen(true);
  };

  const closeIframe = () => {
    const iframe = document.getElementById("orders_myid_iframe");
    if (iframe?.contentWindow) {
      iframe.contentWindow.postMessage({ cmd: "close" }, "*");
    }
    setIframeOpen(false);
  };

  const handleIframeLoad = () => {
    const iframe = document.getElementById("orders_myid_iframe");
    if (iframe?.contentWindow) {
      iframe.contentWindow.postMessage(
        {
          cmd: "open",
          config: { locale: "uz", disableSwitch: true, threshold: 250 },
        },
        "*"
      );
    }
  };

  const getObject = (record) => {
    if (record.apartment) {
      return {
        label: t("order.object.apartment"),
        number:
          record.apartment.apartment_number ?? record.apartment.number ?? "-",
      };
    }
    if (record.parking) {
      return {
        label: t("order.object.parking"),
        number: record.parking.number ?? "-",
      };
    }
    if (record.storage) {
      return {
        label: t("order.object.storage"),
        number: record.storage.number ?? "-",
      };
    }
    return { label: t("order.empty"), number: t("order.empty") };
  };

  const formatPrice = (value, currency) => {
    if (value == null) return "-";
    const num = Number(value);
    if (Number.isNaN(num)) return value;
    return `${num.toLocaleString("ru-RU")} ${
      currency ? currency.toUpperCase() : ""
    }`.trim();
  };

  const columns = [
    {
      title: t("order.field.id"),
      dataIndex: "id",
      key: "id",
      width: 70,
    },
    {
      title: t("order.field.client"),
      key: "client",
      render: (_, record) => record.client?.customerName ?? t("order.empty"),
    },
    {
      title: t("order.field.phone"),
      key: "phone",
      render: (_, record) => record.client?.customerPhone ?? t("order.empty"),
    },
    {
      title: t("order.field.object"),
      key: "object",
      render: (_, record) => {
        const { label, number } = getObject(record);
        return `${label} № ${number}`;
      },
    },
    {
      title: t("order.field.contractNumber"),
      key: "contractNumber",
      render: (_, record) =>
        record.contracts?.[0]?.contractNumber ?? t("order.empty"),
    },
    {
      title: t("order.field.signature"),
      key: "signature",
      render: (_, record) =>
        record.contracts?.[0]?.my_signature ? (
          <Tag color="green">{t("order.signature.signed")}</Tag>
        ) : (
          <Tag color="red">{t("order.signature.unsigned")}</Tag>
        ),
    },
    {
      title: t("order.field.price"),
      key: "price",
      render: (_, record) => formatPrice(record.price, record.priceCurrency),
    },
    {
      title: t("order.field.price_m2"),
      key: "price_m2",
      render: (_, record) =>
        formatPrice(record.price_m2, record.priceCurrency),
    },
    {
      title: t("order.field.discount"),
      dataIndex: "discount",
      key: "discount",
      render: (value) => (value != null ? `${value}%` : t("order.empty")),
    },
    {
      title: t("order.field.downPaymentPercent"),
      dataIndex: "downPaymentPercent",
      key: "downPaymentPercent",
      render: (value) => (value != null ? `${value}%` : t("order.empty")),
    },
    {
      title: t("order.field.paymentDurationPerMonth"),
      dataIndex: "paymentDurationPerMonth",
      key: "paymentDurationPerMonth",
      render: (value) => value ?? t("order.empty"),
    },
    {
      title: t("order.field.type"),
      dataIndex: "type",
      key: "type",
      render: (value) => {
        if (!value) return <Tag>{t("order.empty")}</Tag>;
        const label = formatMessage({
          id: `order.type.${value}`,
          defaultMessage: value,
        });
        return <Tag color={TYPE_COLORS[value] ?? "default"}>{label}</Tag>;
      },
    },
    {
      title: t("order.field.contractType"),
      key: "contractType",
      render: (_, record) => {
        const contract = record.contracts?.[0];
        const isOffline = !!contract?.contract_file_offline;
        return (
          <Tag color={isOffline ? "orange" : "green"}>
            {t(isOffline ? "order.field.contractType.offline" : "order.field.contractType.online")}
          </Tag>
        );
      },
    },
    {
      title: t("order.field.contract"),
      key: "contract",
      render: (_, record) => {
        const contract = record.contracts?.[0];
        const fileUrl =`${process.env.REACT_APP_API_URL}/${contract?.contract_file_offline ?contract?.contract_file_offline:contract?.contract_file}`;
        if (!fileUrl) return t("order.empty");
        return (
          <Button
            type="link"
            href={fileUrl}
            target="_blank"
            download
          >
            {t("button.download")}
          </Button>
        );
      },
    },
    {
      title: t("order.field.user"),
      key: "user",
      render: (_, record) => record.user?.username ?? t("order.empty"),
    },
    {
      title: t("order.field.createdAt"),
      dataIndex: "createdAt",
      key: "createdAt",
      render: (value) =>
        value ? new Date(value).toLocaleString() : t("order.empty"),
    },
    {
      title: "",
      key: "actions",
      fixed: "right",
      render: (_, record) => {
        if (record.signatory?.userId !== user?.id) return null;
        const contract = record.contracts?.[0];
        const disabled = !!contract?.myid_key_director || !!contract?.contract_file_offline;
        return (
          <Button
            type="primary"
            disabled={disabled}
            onClick={() => openIdentify(record)}
          >
            {contract?.myid_key_director ? t("button.manager_signed") : t("button.sign")}
          </Button>
        );
      },
    },
  ];

  return (
    <div style={{ padding: "clamp(10px,3%,30px)" }}>
      <h1>{t("order.page.title")}</h1>
      <br />
      <Space style={{ marginBottom: 16 }} wrap>
        <div>
          <label>{t("order.field.client")}</label>
          <Input
            placeholder={t("order.field.client")}
            allowClear
            onChange={(e) => debouncedSetClient(e.target.value)}
            style={{ width: 250 }}
          />
        </div>
        <div>
          <label>{t("order.field.contractNumber")}</label>
          <Input
            placeholder={t("order.field.contractNumber")}
            allowClear
            onChange={(e) => debouncedSetContract(e.target.value)}
            style={{ width: 250 }}
          />
        </div>
      </Space>
      <Table
        rowKey="id"
        loading={isLoading || myIdLoading}
        columns={columns}
        dataSource={rows}
        scroll={{ x: "max-content" }}
        pagination={{
          current: pagination.current,
          pageSize: pagination.pageSize,
          total,
          showSizeChanger: true,
        }}
        onChange={(p) =>
          setPagination({ current: p.current, pageSize: p.pageSize })
        }
      />

      <Modal
        open={iframeOpen}
        onCancel={closeIframe}
        footer={null}
        width="90%"
        style={{ top: 20 }}
        destroyOnClose
      >
        <div style={{ height: "80vh" }}>
          <iframe
            id="orders_myid_iframe"
            src="https://docs.myid.uz/iframe"
            allow="camera;fullscreen"
            allowFullScreen
            onLoad={handleIframeLoad}
            title="MyID"
            style={{ width: "100%", height: "100%", border: "none" }}
          />
        </div>
      </Modal>
    </div>
  );
}